import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, MenuItem } from "@mui/material";
import { toast } from "react-toastify";
import { TextField, CustomSelect } from "../../../../v2/MUI_custom_component";

import { getLocationAndPincodeAction } from "../../../../v2/Onboarding/auth/action";
import { GetAllCityOfState, GetAllStates } from "../../../Common/API/Geography/action";

const AddressForm = ({
  title,
  value = {},
  onChange,
  errors = {},
  disableState = false,
}) => {
  const company = value.company_address || {};

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [firstSelection, setFirstSelection] = useState(null); // 'location' | 'pincode'
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const emit = (next) => onChange?.(next);
  const setCompanyField = (field, val) =>
    emit({ ...value, company_address: { ...company, [field]: val } });

  const getStates = async () => {
    setLoadingStates(true);
    const { data, error, message } = await GetAllStates();
    setLoadingStates(false);
    if (error) return toast.error(message || "Failed to load states");
    setStates(data.map((s) => ({ label: s.state_name, value: s.id })));
  };

  const getDistricts = async (stateId) => {
    setDistricts([]);
    setLoadingDistricts(true);
    const { data, error, message } = await GetAllCityOfState(stateId);
    setLoadingDistricts(false);
    if (error) return toast.error(message || "Failed to load districts");
    setDistricts(data.map((d) => ({ label: d.district_name, value: d.id })));
  };

  const getLocationPincode = async (
    districtId,
    locationId = null,
    pincodeId = null,
  ) => {
    if (!districtId) return;
    if (pincodeId && valueRef.current.location_id) return;
    if (locationId && valueRef.current.pincode) return;

    setLoadingCities(true);
    const { data, error } = await getLocationAndPincodeAction(districtId, {
      location_id: locationId,
      pincode_id: pincodeId,
    });
    setLoadingCities(false);
    if (error) return toast.error("Failed to load cities and pincodes");

    if (locationId && !valueRef.current.pincode) {
      setPincodes(
        data.pincode_list.map((p) => ({
          label: p.pincode_name,
          value: p.pincode_id,
        })),
      );
    } else if (pincodeId && !valueRef.current.location_id) {
      setCities(
        data.location_list.map((c) => ({
          label: c.location_name,
          value: c.location_id,
        })),
      );
    } else if (!locationId && !pincodeId) {
      setPincodes(
        data.pincode_list.map((p) => ({
          label: p.pincode_name,
          value: p.pincode_id,
        })),
      );
      setCities(
        data.location_list.map((c) => ({
          label: c.location_name,
          value: c.location_id,
        })),
      );
    }
  };


  const nameOf = (list, val) => list.find((o) => o.value === val)?.label || "";

  const handleState = (stateId) => {
    setDistricts([]);
    setCities([]);
    setPincodes([]);
    setFirstSelection(null);
    emit({
      ...value,
      state_id: stateId,
      state_name: nameOf(states, stateId),
      district_id: "",
      district_name: "",
      location_id: "",
      location_name: "",
      pincode: "",
      pincode_name: "",
    });
  };

  const handleDistrict = (districtId) => {
    setCities([]);
    setPincodes([]);
    setFirstSelection(null);
    emit({
      ...value,
      district_id: districtId,
      district_name: nameOf(districts, districtId),
      location_id: "",
      location_name: "",
      pincode: "",
      pincode_name: "",
    });
  };

  const handleLocation = (locationId) => {
    if (!firstSelection) setFirstSelection("location");
    emit({
      ...value,
      location_id: locationId,
      location_name: nameOf(cities, locationId),
      ...(firstSelection === "location"
        ? { pincode: "", pincode_name: "" }
        : {}),
    });
  };

  const handlePincode = (pincodeId) => {
    if (!firstSelection) setFirstSelection("pincode");
    emit({
      ...value,
      pincode: pincodeId,
      pincode_name: nameOf(pincodes, pincodeId),
      ...(firstSelection === "pincode"
        ? { location_id: "", location_name: "" }
        : {}),
    });
  };

  useEffect(() => {
    getStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (states.length > 0 && value.state_id) getDistricts(value.state_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, value.state_id]);

  useEffect(() => {
    if (value.district_id) getLocationPincode(value.district_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.district_id]);

  useEffect(() => {
    if (value.location_id)
      getLocationPincode(value.district_id, value.location_id, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.location_id]);

  useEffect(() => {
    if (value.pincode) getLocationPincode(value.district_id, null, value.pincode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.pincode]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      {title && (
        <Typography variant="h2" sx={{ color: "#333333" }}>
          {title}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
          gap: 2,
        }}>
        <TextField
          label="Address line 1*"
          fullWidth
          placeholder="Enter Address line 1"
          value={company.address_line_1 || ""}
          onChange={(e) => setCompanyField("address_line_1", e.target.value)}
          error={!!errors.address_line_1}
          helperText={errors.address_line_1}
        />
        <TextField
          label="Address line 2*"
          fullWidth
          placeholder="Enter Address line 2"
          value={company.address_line_2 || ""}
          onChange={(e) => setCompanyField("address_line_2", e.target.value)}
          error={!!errors.address_line_2}
          helperText={errors.address_line_2}
        />
        <CustomSelect
          label="State*"
          fullWidth
          displayEmpty
          placeholder="Select State"
          value={value.state_id || ""}
          onChange={(e) => handleState(e.target.value)}
          error={!!errors.state_id}
          helperText={errors.state_id}
          disabled={loadingStates || disableState}>
          {states.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </CustomSelect>

        <CustomSelect
          label="District*"
          fullWidth
          displayEmpty
          placeholder="Select District"
          value={value.district_id || ""}
          onChange={(e) => handleDistrict(e.target.value)}
          error={!!errors.district_id}
          helperText={errors.district_id}
          disabled={!value.state_id || loadingDistricts}>
          {districts.map((d) => (
            <MenuItem key={d.value} value={d.value}>
              {d.label}
            </MenuItem>
          ))}
        </CustomSelect>
        <CustomSelect
          label="City*"
          fullWidth
          displayEmpty
          placeholder="Select City"
          value={value.location_id || ""}
          onChange={(e) => handleLocation(e.target.value)}
          error={!!errors.location_id}
          helperText={errors.location_id}
          disabled={!value.district_id || loadingCities}>
          {cities.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </CustomSelect>
        <CustomSelect
          label="Pincode*"
          fullWidth
          displayEmpty
          placeholder="Select Pincode"
          value={value.pincode || ""}
          onChange={(e) => handlePincode(e.target.value)}
          error={!!errors.pincode}
          helperText={errors.pincode}
          disabled={!value.district_id || loadingCities}>
          {pincodes.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </CustomSelect>
      </Box>
    </Box>
  );
};

export default AddressForm;
